import { useState, useRef, useEffect, useCallback } from 'react';
import { RotateCcw, Check, ChevronDown, ChevronUp, Palette, Eye, Smartphone, Monitor } from 'lucide-react';
import { cn } from '@/lib/utils';
import ProfilePreview from '@/components/dashboard/ProfilePreview';
import { ProfileThemeSection } from '@/components/dashboard/ProfileThemeSection';
import type { PreviewProfile, PreviewLink } from '@/components/dashboard/ProfilePreview';
import { getThemeDNA } from '@commonqr/shared/config/themeDNA';
import { resolveTokens, injectTokens, clearTokens } from '../../lib/designTokens';
import { profileFromDB } from '../../lib/backgroundAnalyzer';
import type { UIStyle, UserCustomization } from '@commonqr/shared/config/themeEngine';

export interface ThemeCustomizerProps {
  profile: PreviewProfile;
  links: PreviewLink[];
  viewMode?: 'mobile' | 'desktop';
  onCustomizationChange?: (customization: UserCustomization) => void;
}

export interface LivePreviewPanelProps {
  profile: PreviewProfile;
  links: PreviewLink[];
  viewMode: 'mobile' | 'desktop';
  onViewModeChange: (v: 'mobile' | 'desktop') => void;
  customization: UserCustomization;
  username: string;
}

export interface CustomisePanelProps {
  onThemeChange: (themeId: string | null, themeImageUrl: string | null) => void;
  profile: PreviewProfile;
  customization: UserCustomization;
  onChange: (patch: Partial<UserCustomization>) => void;
  onApply: () => void;
  onReset: () => void;
}

const UI_STYLES: UIStyle[] = ['solid', 'glass', 'outline', 'soft', 'pill', 'minimal', 'elevated', 'neon', 'elegant', 'futuristic'];

const UI_STYLE_LABELS: Record<UIStyle, string> = { solid: 'Solid', glass: 'Glass', outline: 'Outline', soft: 'Soft', pill: 'Pill', minimal: 'Minimal', elevated: 'Elevated', neon: 'Neon', elegant: 'Elegant', futuristic: 'Futuristic' };

const FONT_OPTIONS = [
  { label: 'Auto (Theme Default)', value: '' },
  { label: 'Inter', value: 'Inter' },
  { label: 'Space Grotesk', value: 'Space Grotesk' },
  { label: 'Cormorant Garamond', value: 'Cormorant Garamond' },
  { label: 'Nunito', value: 'Nunito' },
  { label: 'Playfair Display', value: 'Playfair Display' },
  { label: 'Montserrat', value: 'Montserrat' },
  { label: 'Poppins', value: 'Poppins' },
  { label: 'DM Sans', value: 'DM Sans' },
  { label: 'Raleway', value: 'Raleway' },
  { label: 'Lato', value: 'Lato' },
];

const FONT_SIZE_OPTIONS: { label: string; value: 'auto' | 'sm' | 'md' | 'lg' }[] = [{ label: 'Auto', value: 'auto' }, { label: 'Small', value: 'sm' }, { label: 'Medium', value: 'md' }, { label: 'Large', value: 'lg' }];

export const DEFAULT_CUSTOMIZATION: UserCustomization = {};

export function industryFromThemeId(themeId: string | null | undefined): string {
  if (!themeId) return 'technology-digital';
  const parts = themeId.split('-');
  return parts.length >= 2 ? parts.slice(0, 2).join('-') : themeId;
}

function SectionHeader({ label, open, onToggle }: { label: string; open: boolean; onToggle: () => void }) {
  return (
    <button onClick={onToggle} className="flex items-center justify-between w-full px-4 py-2.5 text-left hover:bg-muted/50 transition-colors border-t border-border">
      <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</span>
      {open ? <ChevronUp className="w-3.5 h-3.5 text-muted-foreground" /> : <ChevronDown className="w-3.5 h-3.5 text-muted-foreground" />}
    </button>
  );
}

function ColorSwatch({ value, onChange, label }: { value: string; onChange: (v: string) => void; label: string }) {
  const inputRef = useRef<HTMLInputElement>(null);
  return (
    <div className="flex items-center gap-2">
      <button onClick={() => inputRef.current?.click()} className="w-8 h-8 rounded-lg border-2 border-border shadow-sm shrink-0 hover:scale-110 transition-transform" style={{ backgroundColor: value }} title={label} />
      <input ref={inputRef} type="color" value={value} onChange={e => onChange(e.target.value)} className="sr-only" />
      <span className="text-xs font-mono text-muted-foreground">{value}</span>
    </div>
  );
}

export function LivePreviewPanel({ profile, links, viewMode, onViewModeChange, customization, username }: LivePreviewPanelProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const dna = getThemeDNA(industryFromThemeId(profile.themeId));
    const colorProfile = profileFromDB(customization.buttonColor ?? '#8b5cf6', 0.12, '#ffffff');
    const tokens = resolveTokens(dna, colorProfile, customization);
    injectTokens(el, tokens);
    return () => clearTokens(el);
  }, [profile.themeId, customization]);
  return (
    <div className="bg-card rounded-2xl border border-border/50 shadow-sm overflow-hidden flex flex-col">
      <div className="px-4 py-2.5 border-b border-border flex items-center justify-between shrink-0">
        <p className="text-xs font-semibold">Live Preview</p>
        <div className="flex items-center gap-2">
          <a href={`/${username}`} target="_blank" rel="noopener noreferrer" className="text-[11px] text-primary hover:underline flex items-center gap-1"><Eye className="w-3 h-3" /> Live</a>
        </div>
      </div>
      <div ref={containerRef} className="bg-muted/40 flex justify-center items-center py-4 overflow-hidden">
        <ProfilePreview profile={{ ...profile, iconColor: customization.iconColor }} links={links} allowReorder={false} hideHeader={true} viewMode={viewMode} />
      </div>
    </div>
  );
}

export function CustomisePanel({ profile, customization, onChange, onApply, onReset, onThemeChange }: CustomisePanelProps) {
  const [openSections, setOpenSections] = useState({ style: true, linkLayout: true, typography: true, colours: true });
  const toggleSection = (k: keyof typeof openSections) => setOpenSections(prev => ({ ...prev, [k]: !prev[k] }));
  const dna = getThemeDNA(industryFromThemeId(profile.themeId));
  const hasChanges = Object.values(customization).some(v => v !== undefined);
  return (
    <div className="bg-card rounded-2xl border border-border/50 shadow-sm overflow-hidden flex flex-col">
      <div className="px-4 py-2.5 border-b border-border flex items-center justify-between shrink-0 sticky top-0 bg-card z-10">
        <span className="text-xs font-semibold flex items-center gap-1.5"><Palette className="w-3.5 h-3.5 text-primary" /> Customise</span>
        <div className="flex items-center gap-2">
          {hasChanges && (<button onClick={onReset} className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1"><RotateCcw className="w-3 h-3" /> Reset</button>)}
          <button onClick={onApply} className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-primary text-primary-foreground text-xs font-medium"><Check className="w-3 h-3" /> Apply</button>
        </div>
      </div>
      <div className="px-4 py-2 bg-muted/50 border-b border-border">
        <p className="text-xs text-muted-foreground">Recommended: <span className="font-semibold text-foreground capitalize">{dna.recommendedUI}</span><span className="mx-1">·</span><span className="italic">{dna.headingFont}</span></p>
      </div>
      <ProfileThemeSection initialThemeId={profile.themeId ?? null} initialThemeImageUrl={profile.themeImageUrl ?? null} onThemeChange={onThemeChange} />
      <SectionHeader label="Button Style" open={openSections.style} onToggle={() => toggleSection('style')} />
      {openSections.style && (
        <div className="px-4 py-3 grid grid-cols-2 gap-1.5">
          {UI_STYLES.map(s => (<button key={s} onClick={() => onChange({ uiStyle: s })} className={cn('px-3 py-2 min-h-[36px] rounded-lg text-xs font-medium border transition-all text-left flex items-center justify-between', (customization.uiStyle ?? dna.recommendedUI) === s ? 'bg-primary text-primary-foreground border-primary' : 'bg-muted text-muted-foreground border-border hover:border-primary/40 hover:text-foreground')}>{UI_STYLE_LABELS[s]}{dna.recommendedUI === s && <span className="text-[10px] opacity-60 ml-1">rec</span>}</button>))}
        </div>
      )}
            <SectionHeader label="Link Layout" open={openSections.linkLayout} onToggle={() => toggleSection('linkLayout')} />
      {openSections.linkLayout && (
        <div className="px-4 py-3 grid grid-cols-3 gap-1.5">
          {(['wide', 'compact', 'icons'] as const).map(layout => (
            <button
              key={layout}
              onClick={() => onChange({ linkLayout: layout })}
              className={cn('px-2 py-2 rounded-lg text-xs font-medium border transition-all text-center flex flex-col items-center gap-1',
                (customization.linkLayout ?? 'wide') === layout
                  ? 'bg-primary text-primary-foreground border-primary'
                  : 'bg-muted text-muted-foreground border-border hover:border-primary/40 hover:text-foreground'
              )}
            >
              {layout === 'wide' && <span className="w-full h-1.5 bg-current rounded-sm opacity-60 block" />}
              {layout === 'compact' && <span className="w-3/4 h-1.5 bg-current rounded-full opacity-60 block" />}
              {layout === 'icons' && <span className="grid grid-cols-3 gap-0.5 w-5">{[0,1,2].map(i=><span key={i} className="w-1.5 h-1.5 bg-current rounded-sm opacity-60 block"/>)}</span>}
              <span className="capitalize">{layout}</span>
            </button>
          ))}
        </div>
      )}
      <SectionHeader label="Typography" open={openSections.typography} onToggle={() => toggleSection('typography')} />
      {openSections.typography && (
        <div className="px-4 py-3 flex flex-col gap-3">
          <div>
            <label className="text-xs font-medium text-muted-foreground block mb-1.5">Font Family</label>
            <select value={customization.fontFamily ?? ''} onChange={e => onChange({ fontFamily: e.target.value || undefined })} className="w-full text-xs border border-border rounded-lg px-3 py-2 bg-card text-foreground focus:outline-none focus:ring-1 focus:ring-primary">
              {FONT_OPTIONS.map(f => <option key={f.value} value={f.value}>{f.value === "" ? `Auto (${dna.headingFont})` : f.label}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground block mb-1.5">Font Size</label>
            <div className="flex gap-1.5">
              {FONT_SIZE_OPTIONS.map(o => (<button key={o.value} onClick={() => onChange({ fontSize: o.value })} className={cn('flex-1 py-1.5 rounded-lg text-xs font-medium border transition-all text-center', (customization.fontSize ?? 'auto') === o.value ? 'bg-primary text-primary-foreground border-primary' : 'bg-muted text-muted-foreground border-border hover:text-foreground')}>{o.label}</button>))}
            </div>
          </div>
        </div>
      )}
      <SectionHeader label="Colours" open={openSections.colours} onToggle={() => toggleSection('colours')} />
      {openSections.colours && (
        <div className="px-4 py-3 flex flex-col gap-3">
          <div>
            <div className="flex items-center justify-between mb-1.5"><label className="text-xs font-medium text-muted-foreground">Text Colour</label>{customization.textColor && <button type="button" onClick={() => onChange({ textColor: undefined })} className="text-[11px] text-muted-foreground hover:text-foreground">Reset</button>}</div>
            <ColorSwatch value={customization.textColor ?? '#ffffff'} onChange={v => onChange({ textColor: v })} label="Text colour" />
          </div>
          <div>
            <div className="flex items-center justify-between mb-1.5"><label className="text-xs font-medium text-muted-foreground">Button Colour</label>{customization.buttonColor && <button type="button" onClick={() => onChange({ buttonColor: undefined })} className="text-[11px] text-muted-foreground hover:text-foreground">Reset</button>}</div>
            <ColorSwatch value={customization.buttonColor ?? '#8b5cf6'} onChange={v => onChange({ buttonColor: v })} label="Button colour" />
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground block mb-1.5">Icon Colour</label>
            <div className="grid grid-cols-2 gap-1.5">
              {([['theme', 'Theme'], ['brand', 'Brand']] as const).map(([value, label]) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => onChange({ iconColor: value === 'theme' ? undefined : value })}
                  className={cn('py-1.5 rounded-lg text-xs font-medium border transition-all text-center', (customization.iconColor ?? 'theme') === value ? 'bg-primary text-primary-foreground border-primary' : 'bg-muted text-muted-foreground border-border hover:text-foreground')}
                >
                  {label}
                </button>
              ))}
            </div>
            <p className="mt-1.5 text-[11px] text-muted-foreground">Brand shows WhatsApp green, YouTube red, Telegram blue and so on.</p>
          </div>
        </div>
      )}
      <div className="h-4 shrink-0" />
    </div>
  );
}

export default function ThemeCustomizer({ profile, links, viewMode = 'mobile', onCustomizationChange }: ThemeCustomizerProps) {
  const [customization, setCustomization] = useState<UserCustomization>(DEFAULT_CUSTOMIZATION);
  const [previewViewMode, setPreviewViewMode] = useState<'mobile' | 'desktop'>(viewMode);
  const handleChange = useCallback((patch: Partial<UserCustomization>) => setCustomization(prev => ({ ...prev, ...patch })), []);
  const handleApply = useCallback(() => onCustomizationChange?.(customization), [customization, onCustomizationChange]);
  const handleReset = useCallback(() => setCustomization(DEFAULT_CUSTOMIZATION), []);
  return (
    <div className="flex flex-col gap-4">
      <LivePreviewPanel profile={profile} links={links} viewMode={previewViewMode} onViewModeChange={setPreviewViewMode} customization={customization} username={profile.username} />
      <CustomisePanel profile={profile} customization={customization} onChange={handleChange} onApply={handleApply} onReset={handleReset} onThemeChange={() => {}} />
    </div>
  );
}
